import { useTranslation } from 'react-i18next'
import { usePushNotifications } from '@/hooks/usePushNotifications'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { Bell, BellOff, AlertTriangle, CheckCircle, XCircle, Info } from 'lucide-react'

interface NotificationSettingsProps {
  restaurantId: string
  userId?: string | undefined
  assistantId?: string | undefined
}

export function NotificationSettings({
  restaurantId,
  userId,
  assistantId,
}: NotificationSettingsProps) {
  const { t } = useTranslation()

  const {
    isSupported,
    permission,
    isSubscribed,
    isLoading,
    enable,
    disable,
    isEnabling,
    isDisabling,
    enableError,
  } = usePushNotifications(restaurantId, { userId, assistantId })

  // Get status badge
  const getStatusBadge = () => {
    if (!isSupported) {
      return (
        <Badge variant="secondary" className="gap-1">
          <XCircle className="h-3 w-3" />
          {t('settings.notifications.unsupported', 'Not Supported')}
        </Badge>
      )
    }

    if (permission === 'denied') {
      return (
        <Badge variant="destructive" className="gap-1">
          <XCircle className="h-3 w-3" />
          {t('settings.notifications.denied', 'Blocked')}
        </Badge>
      )
    }

    if (isSubscribed) {
      return (
        <Badge variant="default" className="gap-1 bg-green-600">
          <CheckCircle className="h-3 w-3" />
          {t('settings.notifications.enabled', 'Enabled')}
        </Badge>
      )
    }

    return (
      <Badge variant="outline" className="gap-1">
        <BellOff className="h-3 w-3" />
        {t('settings.notifications.disabled', 'Disabled')}
      </Badge>
    )
  }

  const handleToggle = async () => {
    if (isSubscribed) {
      await disable()
    } else {
      await enable()
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Bell className="h-5 w-5 text-muted-foreground" />
            <div>
              <CardTitle className="text-lg">
                {t('settings.notifications.title', 'Push Notifications')}
              </CardTitle>
              <CardDescription>
                {t(
                  'settings.notifications.description',
                  'Receive alerts for order cancellations and stock updates'
                )}
              </CardDescription>
            </div>
          </div>
          {getStatusBadge()}
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Browser not supported warning */}
        {!isSupported && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              {t(
                'settings.notifications.notSupportedMessage',
                'Push notifications are not supported in this browser. Please use a modern browser like Chrome, Firefox, or Safari.'
              )}
            </AlertDescription>
          </Alert>
        )}

        {/* Permission denied warning */}
        {isSupported && permission === 'denied' && (
          <Alert variant="destructive">
            <XCircle className="h-4 w-4" />
            <AlertDescription>
              {t(
                'settings.notifications.deniedMessage',
                'Notification permission was blocked. To enable notifications, please update your browser settings and allow notifications for this site.'
              )}
            </AlertDescription>
          </Alert>
        )}

        {/* Error display */}
        {enableError && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              {enableError instanceof Error
                ? enableError.message
                : t('settings.notifications.enableError', 'Failed to enable notifications')}
            </AlertDescription>
          </Alert>
        )}

        {/* Main toggle */}
        {isSupported && permission !== 'denied' && (
          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="space-y-0.5">
              <Label htmlFor="push-toggle" className="text-base font-medium">
                {t('settings.notifications.enableLabel', 'Enable Push Notifications')}
              </Label>
              <p className="text-sm text-muted-foreground">
                {t(
                  'settings.notifications.enableDescription',
                  'Get notified even when the app is closed'
                )}
              </p>
            </div>
            {isLoading ? (
              <LoadingSpinner />
            ) : (
              <Switch
                id="push-toggle"
                checked={isSubscribed}
                onCheckedChange={handleToggle}
                disabled={isEnabling || isDisabling}
              />
            )}
          </div>
        )}

        {/* Notification types info */}
        {isSupported && (
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-muted-foreground">
              {t('settings.notifications.typesTitle', 'You will receive notifications for:')}
            </h4>
            <div className="space-y-2">
              <NotificationType
                icon={<AlertTriangle className="h-4 w-4 text-orange-500" />}
                title={t('settings.notifications.cancellationTitle', 'Order Cancellation Requests')}
                description={t(
                  'settings.notifications.cancellationDesc',
                  'When staff requests to cancel an order'
                )}
              />
              <NotificationType
                icon={<Info className="h-4 w-4 text-yellow-500" />}
                title={t('settings.notifications.lowStockTitle', 'Low Stock Alerts')}
                description={t(
                  'settings.notifications.lowStockDesc',
                  'When ingredient quantity falls below threshold'
                )}
              />
              <NotificationType
                icon={<XCircle className="h-4 w-4 text-red-500" />}
                title={t('settings.notifications.outOfStockTitle', 'Out of Stock Alerts')}
                description={t(
                  'settings.notifications.outOfStockDesc',
                  'When ingredient quantity reaches zero'
                )}
              />
            </div>
          </div>
        )}

        {/* Test notification button (only when subscribed) */}
        {isSubscribed && (
          <div className="pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                // Show a test notification using the browser API
                if ('Notification' in window && Notification.permission === 'granted') {
                  new Notification(t('settings.notifications.testTitle', 'Test Notification'), {
                    body: t(
                      'settings.notifications.testBody',
                      'Push notifications are working correctly!'
                    ),
                    icon: '/icons/icon-192x192.png',
                  })
                }
              }}
            >
              <Bell className="h-4 w-4 me-2" />
              {t('settings.notifications.testButton', 'Send Test Notification')}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// Helper component for notification type display
function NotificationType({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode
  title: string
  description: string
}) {
  return (
    <div className="flex items-start gap-3 rounded-md bg-muted/50 p-3">
      <div className="mt-0.5">{icon}</div>
      <div>
        <p className="text-sm font-medium">{title}</p>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
    </div>
  )
}
